import { FC, useEffect, useState } from 'react';
import { useAppDispatch } from '../../../store/slice';

// PACKAGES
import Moment from 'moment';
import { Spinner } from 'react-bootstrap';

// COMPONENTS
import PaginationCustom from '../../Pagination/Pagination';
import DelayedFadeIn from '../../_Helpers/DelayedFadeIn';
import SvgMask from '../../_Helpers/SvgMask';
import SortButton from './SortButton';

// UTILS
import { guid } from '../../../libs/utils';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';

// TYPES
import apibridge, { GetAssessmentOverviewTypes } from '../../../apibridge';
import { AssessmentSummaryStageOverviewOverviewInfo } from '../../../api/models';

interface AssessmentOverviewTableProps {
	assessmentStageOverviewParams: GetAssessmentOverviewTypes['StageOverviewType'];
	setAssessmentStageOverviewParams: React.Dispatch<
		React.SetStateAction<GetAssessmentOverviewTypes['StageOverviewType']>
	>;
	onStudentClick: (classId: string, studentId: string) => Promise<void>;
}

const StageOverviewTable: FC<AssessmentOverviewTableProps> = ({
	assessmentStageOverviewParams,
	setAssessmentStageOverviewParams,
	onStudentClick
}) => {
	const dispatch = useAppDispatch();

	const [isLoading, setIsLoading] = useState(true);
	const [isReloading, setIsReloading] = useState(false);
	const [stageData, setStageData] = useState<AssessmentSummaryStageOverviewOverviewInfo>();

	const {
		assessmentInfos = [],
		currentPage = 0,
		itemsPerPage = 0,
		stages = [],
		students = [],
		totalItems = 0,
		totalPages = 0
	} = stageData || {};

	const getStageData = async () => {
		setIsReloading(true);

		const response = await apibridge.getAssessmentSummaryStageOverview(assessmentStageOverviewParams);
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setStageData(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Grapheme-phoneme overview error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
		setIsReloading(false);
	};

	useEffect(() => {
		getStageData();
	}, [assessmentStageOverviewParams]); // eslint-disable-line react-hooks/exhaustive-deps

	// Reset page to 1 when params change, otherwise pagination will break
	useEffect(() => {
		if ((assessmentStageOverviewParams.pageNumber || 1) !== 1) {
			setAssessmentStageOverviewParams({ ...assessmentStageOverviewParams, pageNumber: 1 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		assessmentStageOverviewParams._class,
		assessmentStageOverviewParams.assessmentPeriodId,
		assessmentStageOverviewParams.benchmark
	]);

	return (
		<div className="assessment-general-table stage-overview-table">
			{isLoading ? (
				<div className="d-flex align-items-center justify-content-center position-relative w-100">
					<DelayedFadeIn>
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Loading...</span>
						</Spinner>
					</DelayedFadeIn>
				</div>
			) : (
				<>
					<div className={`table-container text-shades-800 ${isReloading ? 'opacity-75' : ''}`}>
						<div className="table-col-students">
							<div className="student-row-heading">
								<div className="student-name-cell d-flex align-items-center justify-content-between gap-3">
									<span className="body-tiny">
										<strong>Student name</strong>
									</span>

									<SortButton
										type="Student"
										params={assessmentStageOverviewParams}
										setParams={setAssessmentStageOverviewParams}
										disabled={isReloading}
									/>
								</div>

								<div className="last-recorded-cell">
									<span className="body-tiny">
										<strong>Last recorded</strong>
									</span>

									<SortButton
										type="LastRecorded"
										params={assessmentStageOverviewParams}
										setParams={setAssessmentStageOverviewParams}
										disabled={isReloading}
									/>
								</div>
							</div>

							{/* Student name List */}
							{students.length > 0 ? (
								<div className="student-buttons-wrapper">
									{students.map((student, studentIndex) => {
										return (
											<button
												className={`btn btn-student student-row d-flex justify-content-between ${
													studentIndex % 2 === 0 ? 'bg-white' : ''
												}`}
												key={studentIndex}
												onClick={() => onStudentClick(student.classId || '', student.id || '')}
											>
												<div className="student-name-cell d-flex flex-column justify-content-center">
													<span className="student-name body-small">
														<strong>
															{student.firstName} {student.lastName}
														</strong>
													</span>

													<span className="body-mini fw-semibold opacity-50">{student.className}</span>
												</div>

												<div className="last-recorded-cell">
													<span className="body-tiny fw-semibold">
														{Moment(student.lastRecorded).format('DD/MM/YY')}
													</span>

													<div className="edit-btn">
														<SvgMask path="/svg/edit.svg" width={24} height={24} />
													</div>
												</div>
											</button>
										);
									})}
								</div>
							) : (
								<div className="student-row d-flex justify-content-between">
									<div className="student-name-cell d-flex flex-column justify-content-center">
										<p className="my-3">No results</p>
									</div>
								</div>
							)}
						</div>

						<div className="table-col-content d-flex scroll-btn-anchor w-100">
							<div className="stages-scroll-container w-100">
								{/* Header */}
								<div className="d-flex w-100">
									{assessmentInfos.map((info, infoIndex) => {
										const { key, label } = info;
										return (
											<div key={infoIndex} className="stage-col border-bottom-0 border-end-0">
												<div className="stage-name justify-content-between px-2">
													<span className="stage-progress-name">
														{label?.toLowerCase() === 'gpk' ? 'Grapheme-Phoneme Knowledge' : label}
													</span>

													<SortButton
														type={key === 'GraphemePhonemeKnowledge' ? 'Gpk' : key}
														params={assessmentStageOverviewParams}
														setParams={setAssessmentStageOverviewParams}
														disabled={isReloading}
													/>
												</div>
											</div>
										);
									})}
								</div>

								{students.length > 0 ? (
									<div className="table-row-wrapper">
										{students.map((student, studentIndex) => {
											const { stageProgress = {} } = student;
											const { graphemePhoneme = '', reading = '', spelling = '' } = stageProgress;
											const zebraStriped = studentIndex % 2 === 0 ? 'alternate-bg-white' : '';
											return (
												<div key={studentIndex} className="table-row student-row d-flex w-100 border-end-0">
													<div
														className={`assessment-status-cell student-cell-wrapper d-flex align-items-center ${zebraStriped}`}
													>
														<div className={`stage-display stage-${graphemePhoneme}`}>
															<strong>{graphemePhoneme ? `Stage ${graphemePhoneme}` : '-'}</strong>
														</div>
													</div>

													<div
														className={`assessment-status-cell student-cell-wrapper d-flex align-items-center ${zebraStriped}`}
													>
														<div className={`stage-display stage-${reading}`}>
															<strong>{reading ? `Stage ${reading}` : '-'}</strong>
														</div>
													</div>

													<div
														className={`assessment-status-cell student-cell-wrapper d-flex align-items-center ${zebraStriped}`}
													>
														<div className={`stage-display stage-${spelling}`}>
															<strong>{spelling ? `Stage ${spelling}` : '-'}</strong>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								) : null}
							</div>
						</div>
					</div>

					{totalPages > 1 && (
						<div className="mt-5 d-flex justify-content-between align-items-center">
							<PaginationCustom
								pageNumber={assessmentStageOverviewParams.pageNumber || 1}
								totalPages={totalPages}
								pageSwitchCallback={(newPageNumber) => {
									setAssessmentStageOverviewParams({
										...assessmentStageOverviewParams,
										pageNumber: newPageNumber
									});
								}}
							/>

							<div className="body-small">
								<strong>
									Showing {itemsPerPage * (currentPage - 1) + 1} -{' '}
									{currentPage < totalPages ? itemsPerPage * currentPage : totalItems} of {totalItems}{' '}
									{totalItems === 1 ? 'student' : 'students'}
								</strong>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default StageOverviewTable;
